import { useTranslation } from 'react-i18next';

import { ChartsIcon } from '@/shared/assets/icons/charts-icon';
import { EntitlementsIcon } from '@/shared/assets/icons/entitlements-icon';
import { FolderIcon } from '@/shared/assets/icons/folder-icon';
import { GaugeIcon } from '@/shared/assets/icons/gauge-icon';
import { SettingsIcon } from '@/shared/assets/icons/settings-icon';
import { SheetIcon } from '@/shared/assets/icons/sheet-icon';
import { TenantIcon } from '@/shared/assets/icons/tenant-icon';
import { UserIcon } from '@/shared/assets/icons/user-icon';
import { UserRoleIcon } from '@/shared/assets/icons/user-role-icon';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import {
  NavigationItemType,
  TNavigationSection,
} from '@/shared/features/sidebar/sidebar.types';
import { selectIsDevUser } from '@/shared/redux/api/auth/auth-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';

const visibleItems = (
  items: (NavigationItemType | false)[],
): NavigationItemType[] =>
  items.filter((item): item is NavigationItemType => Boolean(item));

export const useBackofficeSidebarSections = (): TNavigationSection[] => {
  const { t } = useTranslation();
  const { can } = usePermissionCheck();
  const isDevUser = useAppSelector(selectIsDevUser);

  const mainSection: TNavigationSection = {
    children: visibleItems([
      can('backofficeTenant:readTenant') && {
        name: t('pages:tenants'),
        icon: <TenantIcon />,
        link: '/',
      },
      can('backofficeGroup:readGroup') && {
        name: t('pages:groups'),
        icon: <FolderIcon />,
        link: '/groups',
      },
    ]),
  };

  const adminSection: TNavigationSection = {
    name: t('pages:admin'),
    children: visibleItems([
      can('backofficeTenant:exportTenantBilling') && {
        name: t('pages:data_exports'),
        icon: <SheetIcon />,
        link: '/data-exports',
      },
      can('backofficeUser:readUser') && {
        name: t('pages:users'),
        icon: <UserIcon />,
        link: '/users',
      },
      can('backofficeUserRole:readUserRole') && {
        name: t('pages:roles'),
        icon: <UserRoleIcon />,
        link: '/roles',
      },
    ]),
  };

  // Its own section rather than entries inside Admin: these are gated by the
  // `custom:isDeveloper` Cognito claim rather than by RBAC, so grouping them
  // keeps that distinction visible. The EC2 Orchestrator Config also needs its
  // RBAC permission, since its route checks both. Empty sections are dropped
  // below, so for a non-developer the section and its divider disappear.
  const developerSection: TNavigationSection = {
    name: t('pages:developer'),
    children: visibleItems([
      isDevUser && {
        name: t('pages:ideaas'),
        icon: <ChartsIcon />,
        link: '/ideaas',
      },
      isDevUser &&
        can('backofficeEc2OrchestratorConfig:readConfig') && {
          name: t('pages:ec2_orchestrator_config'),
          icon: <SettingsIcon />,
          link: '/ec2-orchestrator-config',
        },
      isDevUser && {
        name: t('pages:feature_flag_catalog'),
        icon: <EntitlementsIcon />,
        link: '/feature-flag-catalog',
      },
      isDevUser && {
        name: t('pages:sre_hub'),
        icon: <GaugeIcon />,
        link: '/sre-hub',
      },
    ]),
  };

  const allSections = [mainSection, adminSection, developerSection];

  return allSections.flatMap((section) => {
    if (!section || section.hide || section.children.length === 0) {
      return [];
    }

    return [section];
  });
};
